// Expanded venue card body — shown inline in the venue list when the
// guest taps a card. Renders the photo carousel, event banner, and
// action row (social / directions / message).

import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Clock3, Navigation, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { VenueFavoriteButton } from './VenueFavoriteButton';
import { ImageCarousel } from './ImageCarousel';
import { SocialPopup } from './SocialButtons';
import { VenueEventBanner } from './VenueEventBanner';
import { getVenueImage, getVenueOwnPhotos, venueImagePool } from './venueImage';
import {
  formatVenueCardDescription,
  formatVenueCategoryLabel,
  venueOpenStatusLabel,
} from './venueFormatters';
import { openDirections } from './directionsIntent';
import {
  MessageBubbleSvg,
  SocialMediaSvg,
  actionButtonSurface,
} from './mapSurfaceStyles';

const font = "'Inria Serif', serif";
const disabledStyle = { opacity: 0.45, cursor: 'not-allowed' };

export function ExpandedVenueCard({
  venue, idx, onClose, onMessage, onOpenReviews, venueEvents, hasActivePass = true,
}) {
  const [showSocials, setShowSocials] = useState(false);
  // Prefer the venue's own uploaded photos in order. Only fall back to
  // the shared image pool when the venue has zero uploaded photos.
  const images = useMemo(() => {
    const own = getVenueOwnPhotos(venue);
    if (own.length > 0) return own;
    const mainImage = getVenueImage(venue, idx);
    const extras = venueImagePool.filter((image) => image !== mainImage).slice(0, 3);
    return [mainImage, ...extras];
  }, [venue, idx]);
  const hasSocials = venue.website || venue.instagram || venue.facebook || venue.tiktok;

  const btnStyle = {
    width: 46, height: 46, borderRadius: 18,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', flexShrink: 0,
    ...actionButtonSurface,
  };
  const handleDirectionsTap = (event) => {
    event.stopPropagation();
    openDirections(venue);
  };
  const handleMessageTap = (event) => {
    event.stopPropagation();
    if (!hasActivePass) {
      toast('Only guests with an active QR pass can message venues.', {
        duration: 3500,
        id: `msg-gate-${venue.id}`,
      });
      return;
    }
    onMessage(venue);
  };

  return (
    <div data-testid={`venue-expanded-${venue.id}`} className="premium-card" style={{ borderRadius: 28, overflow: 'visible', position: 'relative' }}>
      <div style={{ position: 'relative', borderRadius: '28px 28px 0 0', overflow: 'hidden' }}>
        <ImageCarousel images={images} height={228} videoUrl={venue.intro_video_url || ''} />
        <div
          style={{ position: 'absolute', top: 10, left: 10, zIndex: 5 }}
          onClick={(event) => event.stopPropagation()}
        >
          <VenueFavoriteButton venueId={venue.id} testIdPrefix="venue-expanded-favorite" />
        </div>
        <button onClick={(event) => { event.stopPropagation(); onClose(); }} data-testid={`venue-close-${venue.id}`} style={{ position: 'absolute', top: 10, right: 10, zIndex: 5, width: 32, height: 32, borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <X size={18} color="#fff" />
        </button>
      </div>

      <div style={{ padding: '16px 18px 18px', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontFamily: font, fontSize: 23, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.06 }}>{venue.name}</p>
            <p style={{ fontFamily: font, fontSize: 14, fontWeight: 700, color: 'var(--text-2)', margin: '6px 0 0', letterSpacing: '0.01em', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{formatVenueCategoryLabel(venue)}</p>
            {formatVenueCardDescription(venue) ? (
              <p style={{ fontFamily: font, fontSize: 13, fontWeight: 500, color: 'var(--text-3)', margin: '3px 0 0', lineHeight: 1.4, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{formatVenueCardDescription(venue)}</p>
            ) : null}
          </div>
          <button
            type="button"
            data-testid={`venue-expanded-rating-${venue.id}`}
            onClick={(event) => { event.stopPropagation(); onOpenReviews?.(); }}
            aria-label="See ratings & reviews"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 999, background: 'rgba(112, 63, 246, 0.08)', color: 'var(--accent-text-soft)', fontFamily: font, fontSize: 13, fontWeight: 700, flexShrink: 0, border: 'none', cursor: 'pointer' }}
          >
            {Number(venue.rating) > 0 ? (
              <>
                <Star size={14} fill="#F4C54E" color="#F4C54E" />
                {venue.rating}
              </>
            ) : 'New'}
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, marginBottom: 12 }}>
          <div data-testid={`venue-expanded-open-state-${venue.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 999, background: venue.is_open_now === false ? 'rgba(214, 50, 50, 0.08)' : 'rgba(112, 63, 246, 0.08)', color: venue.is_open_now === false ? '#B33232' : '#5A4FA3', fontFamily: font, fontSize: 13, fontWeight: 700 }}>
            <Clock3 size={13} />
            {venueOpenStatusLabel(venue)}
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 999, background: 'rgba(17, 163, 82, 0.08)', color: '#12914E', fontFamily: font, fontSize: 13, fontWeight: 700 }}>
            <Navigation size={13} />
            Directions ready
          </div>
        </div>

        <VenueEventBanner events={venueEvents} />

        <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 52px', alignItems: 'center', gap: 10, marginTop: 14, position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <motion.button
              style={btnStyle}
              data-testid={`venue-social-${venue.id}`}
              onClick={(event) => { event.stopPropagation(); if (hasSocials) setShowSocials(!showSocials); }}
              whileTap={{ scale: 0.92 }}
              whileHover={{ scale: 1.04 }}
              transition={{ type: 'spring', stiffness: 480, damping: 22 }}
            >
              <SocialMediaSvg size={21} />
            </motion.button>
            <AnimatePresence>
              {showSocials ? <SocialPopup venue={venue} /> : null}
            </AnimatePresence>
          </div>
          <motion.button
            onClick={handleDirectionsTap}
            data-testid={`venue-directions-expanded-${venue.id}`}
            whileTap={{ scale: 0.97 }}
            whileHover={{ scale: 1.015 }}
            transition={{ type: 'spring', stiffness: 480, damping: 24 }}
            style={{ width: '100%', borderRadius: 18, color: 'var(--accent-text-soft)', fontFamily: font, fontSize: 15, fontWeight: 700, padding: '13px 0', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'var(--accent-surface)', border: '1px solid var(--card-border)', boxShadow: 'var(--premium-shadow-nested)' }}
          >
            <Navigation size={16} color="currentColor" strokeWidth={2.2} />
            Get directions
          </motion.button>
          {venue.allow_messages !== false ? (
            <motion.button
              style={{ ...btnStyle, ...(hasActivePass ? {} : disabledStyle) }}
              data-testid={`venue-message-${venue.id}`}
              aria-disabled={!hasActivePass}
              onClick={handleMessageTap}
              whileTap={hasActivePass ? { scale: 0.92 } : undefined}
              whileHover={hasActivePass ? { scale: 1.04 } : undefined}
              transition={{ type: 'spring', stiffness: 480, damping: 22 }}
            >
              <MessageBubbleSvg size={21} />
            </motion.button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
